import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import { ZoomIn, Loader2, Check } from 'lucide-react';

const OUTPUT_SIZE = 1000;

const cropToBlob = (src, area) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('crop failed'))), 'image/jpeg', 0.9);
  };
  img.onerror = reject;
  img.src = src;
});

export const ImageCropDialog = ({ src, onCancel, onConfirm }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setArea(pixels), []);

  const confirm = async () => {
    setBusy(true);
    try {
      await onConfirm(await cropToBlob(src, area));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onCancel()}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-lg z-[70]" data-testid="image-crop-dialog">
        <DialogHeader>
          <DialogTitle>Recortar imagem</DialogTitle>
        </DialogHeader>
        <div className="relative w-full aspect-square bg-zinc-800 rounded-lg overflow-hidden">
          <Cropper image={src} crop={crop} zoom={zoom} aspect={1} minZoom={1} maxZoom={4}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} restrictPosition />
        </div>
        <div className="flex items-center gap-3 py-2">
          <ZoomIn className="w-4 h-4 text-zinc-400" />
          <Slider value={[zoom]} min={1} max={4} step={0.05} onValueChange={([z]) => setZoom(z)} data-testid="crop-zoom-slider" />
        </div>
        <p className="text-xs text-zinc-500 -mt-2">Arraste a imagem para centrar e use o zoom para ajustar.</p>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onCancel} disabled={busy}
            className="flex-1 bg-zinc-800 text-white border-zinc-600 hover:bg-zinc-700" data-testid="crop-cancel-btn">Cancelar</Button>
          <Button type="button" onClick={confirm} disabled={busy || !area}
            className="flex-1 bg-sonangol-orange hover:bg-orange-600 text-white" data-testid="crop-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />Aplicar</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropDialog;
